import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import { useUser } from '../context/UserContext';
import MessagePanel from '../components/whatsapp/MessagePanel';
import MessageItem from '../components/whatsapp/MessageItem';
import ConversationPanel from '../components/whatsapp/ConversationPanel';
import MessageInputPanel from '../components/whatsapp/MessageInputPanel';
import TemplateMsgModal from '../components/whatsapp/TemplateMsgModal';
import ConversationHeader from '../components/whatsapp/ConversationHeader';
import ToastNotification from '../components/ToastNotification';
import { getInitials, getFileType, getFileName, extractMessageContent, formatMessageText } from '../utils/messageHelpers';
import { getCSWStatus } from '../utils/conversationHelpers';
import { getFileTypeFromFile, clearFileSelection } from '../utils/fileHelpers';
import { Conversation, Message } from '../types/wa_messages';

const PAGE_SIZE = 15;



const WhatsAppManager: React.FC = () => {
  const { accessToken, setUserAndToken, user } = useUser();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [conversationsTotal, setConversationsTotal] = useState(0);
  const [conversationsPage, setConversationsPage] = useState(1);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [conversationsError, setConversationsError] = useState('');

  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);

  const [messages, setMessages] = useState<Message[]>([]);
  const [messagesTotal, setMessagesTotal] = useState(0);
  const [messagesPage, setMessagesPage] = useState(1);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState('');

  const [search, setSearch] = useState('');
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [isLoadingNewer, setIsLoadingNewer] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [atTop, setAtTop] = useState(false);
  const [atBottom, setAtBottom] = useState(false);

  const scrollAdjustmentRef = useRef<number | null>(null);
  const initialLoadRef = useRef(true);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const sentinelTopRef = useRef<number | null>(null);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  
  // Refs for handling race conditions in fetchConversationDetail
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef<number>(0);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');

  // Add state for file upload
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadedMediaUrl, setUploadedMediaUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [gcsUri, setGcsUri] = useState<string | null>(null);



  // Add at the top, after other useState hooks
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);

  // Add state for template modal and toast
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any | null>(null);
  const [templateVars, setTemplateVars] = useState<{ [key: string]: string }>({});
  const [toastMsg, setToastMsg] = useState<string | null>(null);



  // Move this here so it's available everywhere in the component
  const selectedConversation = conversations.find(c => c.id === selectedConversationId) || null;
  const [selectedConversationDetail, setSelectedConversationDetail] = useState<Conversation | null>(null);

  // Add at the top, after other useState hooks
  const [waTemplates, setWaTemplates] = useState<any[]>([]);
  const [waTemplatesLoading, setWaTemplatesLoading] = useState(false);
  const [waTemplatesError, setWaTemplatesError] = useState('');

  // Fetch WhatsApp templates from backend on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      setWaTemplatesLoading(true);
      setWaTemplatesError('');
      try {
        const resp = await authFetch(
          (process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000') + '/api/v1/admin/wa_templates',
          {},
          accessToken,
          setUserAndToken
        );
        if (resp.ok) {
          const data = await resp.json();
          setWaTemplates(data.templates || []);
        } else {
          setWaTemplatesError('Failed to load templates');
        }
      } catch (e) {
        setWaTemplatesError('Failed to load templates');
      } finally {
        setWaTemplatesLoading(false);
      }
    };
    fetchTemplates();
  }, [accessToken]);

  // Handle conversation parameter from URL
  useEffect(() => {
    const conversationId = searchParams.get('conversation');
    if (conversationId) {
      setSelectedConversationId(conversationId);
    }
  }, [searchParams]);

  // Fetch conversations
  useEffect(() => {
    const fetchConversations = async () => {
      setConversationsLoading(true);
      setConversationsError('');
      try {
        const resp = await authFetch(
          (process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000') +
          `/api/v1/admin/conversations?page=${conversationsPage}&page_size=${PAGE_SIZE}`,
          {},
          accessToken,
          setUserAndToken
        );
        if (resp.ok) {
          const data = await resp.json();
          setConversations(data.conversations || data.data?.conversations || []);
          setConversationsTotal(data.total || data.data?.total || 0);
          // Only set default conversation if no conversation is currently selected and no URL param
          if (conversationsPage === 1 && 
              (data.conversations?.length || data.data?.conversations?.length) &&
              !selectedConversationId && 
              !searchParams.get('conversation')) {
            const latest = (data.conversations || data.data?.conversations)[0];
            setSelectedConversationId(latest?.id || null);
          }
        } else {
          setConversationsError('Failed to load conversations.');
        }
      } catch (e) {
        setConversationsError('Failed to load conversations.');
      } finally {
        setConversationsLoading(false);
      }
    };
    fetchConversations();
    // eslint-disable-next-line
  }, [conversationsPage, accessToken]);

  // Fetch messages for selected conversation
  useEffect(() => {
    if (!selectedConversationId) {
      setMessages([]);
      setMessagesTotal(0);
      setSelectedConversationDetail(null);
      return;
    }
    
    // Abort any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    // Increment request ID to track this specific request
    const currentRequestId = ++requestIdRef.current;
    
    // Also fetch conversation detail enrichment
    const fetchConversationDetail = async () => {
      try {
        const resp = await authFetch(
          (process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000') +
          `/api/v1/admin/conversations/${selectedConversationId}`,
          { signal: abortController.signal },
          accessToken,
          setUserAndToken
        );
        
        // Check if this is still the current request
        if (currentRequestId !== requestIdRef.current) {
          return; // Stale request, ignore response
        }
        
        if (resp.ok) {
          const data = await resp.json();
          setSelectedConversationDetail(data);
          // Also merge into list copy for UI components that read from selectedConversation
          setConversations(prev => prev.map(c => (c.id === selectedConversationId ? { ...c, ...data } : c)));
        }
      } catch (e) {
        // Handle AbortError separately - don't treat it as an error
        if (e instanceof Error && e.name === 'AbortError') {
          return; // Request was aborted, this is expected
        }
        // For other errors, we can choose to ignore them as before
        // or handle them appropriately based on requirements
      }
    };
    fetchConversationDetail();
    const fetchMessages = async () => {
      setMessagesLoading(true);
      setMessagesError('');
      try {
        const resp = await authFetch(
          (process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000') +
          `/api/v1/admin/conversations/${selectedConversationId}/messages?page=1&page_size=${PAGE_SIZE}`,
          {},
          accessToken,
          setUserAndToken
        );
        if (resp.ok) {
          const data = await resp.json();
          setMessages(data.messages || data.data?.messages || []);
          setMessagesTotal(data.total || data.data?.total || 0);
        } else {
          setMessagesError('Failed to load messages.');
        }
      } catch (e) {
        setMessagesError('Failed to load messages.');
      } finally {
        setMessagesLoading(false);
      }
    };
    fetchMessages();
    
    // Cleanup function to abort outstanding requests
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
    // eslint-disable-next-line
  }, [selectedConversationId, accessToken]);

  // When selected conversation changes, reset messages page
  useEffect(() => {
    setMessagesPage(1);
  }, [selectedConversationId]);





  // Scroll position tracking
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    setAtTop(container.scrollTop === 0);
    setAtBottom(container.scrollHeight - container.scrollTop === container.clientHeight);
  }, []);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    initialLoadRef.current = true;
  }, [selectedConversationId]);

  useEffect(() => {
    if (initialLoadRef.current && messages.length > 0) {
      const container = messagesContainerRef.current;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
      initialLoadRef.current = false;
    }
  }, [messages, selectedConversationId]);

  // Load older messages (prepend)
  const loadOlderMessages = async () => {
    if (isLoadingOlder || messages.length >= messagesTotal) return;
    setIsLoadingOlder(true);
    // Record the top position of the sentinel (first visible message)
    if (sentinelRef.current) {
      sentinelTopRef.current = sentinelRef.current.getBoundingClientRect().top;
    } else {
      sentinelTopRef.current = null;
    }
    const nextPage = messagesPage + 1;
    const resp = await authFetch(
      (process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000') +
      `/api/v1/admin/conversations/${selectedConversationId}/messages?page=${nextPage}&page_size=${PAGE_SIZE}`,
      {},
      accessToken,
      setUserAndToken
    );
    if (resp.ok) {
      const data = await resp.json();
      setMessages(prev => [...data.messages, ...prev]);
      setMessagesPage(nextPage);
    }
    setIsLoadingOlder(false);
  };

  // Load newer messages (append)
  const loadNewerMessages = async () => {
    if (isLoadingNewer || messages.length >= messagesTotal || messagesPage <= 1) return;
    setIsLoadingNewer(true);
    const prevPage = messagesPage - 1;
    const resp = await authFetch(
      (process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000') +
      `/api/v1/admin/conversations/${selectedConversationId}/messages?page=${prevPage}&page_size=${PAGE_SIZE}`,
      {},
      accessToken,
      setUserAndToken
    );
    if (resp.ok) {
      const data = await resp.json();
      setMessages((prev) => [...prev, ...data.messages]);
      setMessagesPage(prevPage);
    }
    setIsLoadingNewer(false);
  };



  // After messages update, apply the scroll adjustment
  useEffect(() => {
    if (scrollAdjustmentRef.current !== null && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = scrollAdjustmentRef.current;
      scrollAdjustmentRef.current = null;
    }
  }, [messages]);

  // After messages update, adjust scroll so the sentinel stays at the same position
  useEffect(() => {
    if (sentinelTopRef.current !== null && sentinelRef.current && messagesContainerRef.current) {
      const newTop = sentinelRef.current.getBoundingClientRect().top;
      const diff = newTop - sentinelTopRef.current;
      messagesContainerRef.current.scrollTop += diff;
      sentinelTopRef.current = null;
    }
  }, [messages]);



  // Move sendMessageWithMedia to top-level inside the component
  const sendMessageWithMedia = async (mediaUrl: string | null, gcsUri?: string) => {
    let msgType = 'text';
    let body: any = {};
    if (mediaUrl && selectedFile) {
      const fileType = getFileTypeFromFile(selectedFile);
      msgType = fileType === 'image' ? 'image' : (fileType === 'pdf' ? 'document' : 'document');
      if (msgType === 'document') {
        body = {
          type: 'document',
          media_url: mediaUrl, // signed URL for WhatsApp API
          gcs_uri: gcsUri, // gsutil URL for database storage
          caption: newMessage.trim() || undefined,
          filename: selectedFile.name
        };
      } else {
        body = {
          type: msgType,
          media_url: mediaUrl, // signed URL for WhatsApp API
          gcs_uri: gcsUri, // gsutil URL for database storage
          caption: newMessage.trim() || undefined,
        };
      }
      console.log('[DEBUG] Sending message with body:', body);
    } else {
      body = {
        type: 'text',
        text: newMessage.trim(),
      };
    }
    // Add context if replying
    if (replyingTo && replyingTo.whatsapp_message_id) {
      body.context = { message_id: replyingTo.whatsapp_message_id };
    }
    const resp = await authFetch(
      (process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000') +
      `/api/v1/admin/conversations/${selectedConversationId}/send_message`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      },
      accessToken,
      setUserAndToken
    );
    if (resp.ok) {
      // Optimistically add the message to the UI
      const now = new Date().toISOString();
      setMessages(prev => [
        ...prev,
        {
          id: 'temp-' + now,
          conversation_id: selectedConversationId || '',
          direction: 'outbound',
          channel: 'whatsapp',
          message_type: msgType,
          content: newMessage.trim() || '',
          media_urls: msgType === 'image' && mediaUrl ? [mediaUrl] : [],
          backup_media_urls: msgType === 'image' && mediaUrl ? [mediaUrl] : [],
          timestamp: now,
          delivery_status: 'sent',
          admin_name: user?.name || undefined,
          payload_json: selectedFile ? {
            type: msgType,
            [msgType]: {
              link: mediaUrl,
              filename: selectedFile.name,
              ...(newMessage.trim() ? { caption: newMessage.trim() } : {})
            }
          } : undefined,
          quoted_wa_msg: replyingTo
            ? {
                content: replyingTo.content,
                message_type: replyingTo.message_type,
                backup_media_urls: replyingTo.backup_media_urls,
                filename: replyingTo.media_urls && replyingTo.media_urls.length > 0 ? getFileName(replyingTo.media_urls[0]) : undefined,
              }
            : undefined,
        } as Message
      ]);
      setNewMessage('');
      clearFileSelection(
        setSelectedFile,
        setFilePreviewUrl,
        setUploadedMediaUrl,
        setGcsUri,
        setUploadProgress,
        setUploading,
        setUploadError
      );
      setReplyingTo(null); // Clear reply after sending
    } else {
      const err = await resp.json().catch(() => ({}));
      setSendError(err.detail || 'Failed to send message.');
    }
    setSending(false);
  };

  // In handleSendMessage, just call sendMessageWithMedia as before
  const handleSendMessage = async () => {
    if (!selectedConversationId || (!newMessage.trim() && !selectedFile)) return;
    setSending(true);
    setSendError('');
    let uploadedUrl = uploadedMediaUrl;
    try {
      // If file is selected, upload it first
      if (selectedFile) {
        setUploading(true);
        setUploadProgress(0);
        const formData = new FormData();
        formData.append('file', selectedFile, selectedFile.name); // preserve original name
        // Optionally, send filename as a separate field if backend expects it
        // formData.append('filename', selectedFile.name);
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000'}/api/v1/admin/conversations/${selectedConversationId}/upload_media`);
        xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            setUploadProgress(Math.round((event.loaded / event.total) * 100));
          }
        };
        xhr.onload = () => {
          setUploading(false);
          if (xhr.status === 200) {
            const resp = JSON.parse(xhr.responseText);
            console.log('[DEBUG] Upload response:', resp);
            // Use signed_url for WhatsApp API, but store gcs_uri for database
            uploadedUrl = resp.signed_url; // Use signed URL for WhatsApp API
            setUploadedMediaUrl(resp.signed_url); // Store signed URL for preview
            // Store gcs_uri for database storage
            setGcsUri(resp.gcs_uri);
            console.log('[DEBUG] Set gcsUri to:', resp.gcs_uri);
            // Pass gcs_uri directly to avoid timing issue with state update
            sendMessageWithMedia(uploadedUrl, resp.gcs_uri);
          } else {
            setUploadError('Failed to upload file.');
            setSending(false);
          }
        };
        xhr.onerror = () => {
          setUploading(false);
          setUploadError('Failed to upload file.');
          setSending(false);
        };
        xhr.send(formData);
        return; // Wait for upload to finish before sending message
      } else {
        // No file, just send text
        await sendMessageWithMedia(null, undefined);
      }
    } catch (e) {
      setSendError('Failed to send message.');
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-96px)] w-full text-left overflow-hidden">
      <div className="flex-1 flex justify-center items-start w-full h-full relative overflow-hidden">
        <div className="w-full max-w-8xl mx-auto flex gap-4 h-full">
          {/* Conversation Panel */}
          <ConversationPanel
            conversations={conversations}
            conversationsTotal={conversationsTotal}
            conversationsLoading={conversationsLoading}
            conversationsError={conversationsError}
            selectedConversationId={selectedConversationId}
            search={search}
            setSearch={setSearch}
            setSelectedConversationId={setSelectedConversationId}
          />
          {/* Messages Panel */}
          <div className="bg-white rounded-2xl border border-slate-200 flex-1 flex flex-col h-full min-h-0 relative">
            {/* Conversation Header */}
            <ConversationHeader
              selectedConversationId={selectedConversationId}
              selectedConversation={selectedConversationDetail || selectedConversation}
            />
            {/* Scrollable Messages List */}
            <MessagePanel
              messages={messages}
              selectedConversation={selectedConversation}
              atTop={atTop}
              atBottom={atBottom}
              messagesLoading={messagesLoading}
              messagesError={messagesError}
              messagesTotal={messagesTotal}
              loadOlderMessages={loadOlderMessages}
              loadNewerMessages={loadNewerMessages}
              isLoadingOlder={isLoadingOlder}
              isLoadingNewer={isLoadingNewer}
              messagesContainerRef={messagesContainerRef}
              renderMessage={(msg, idx) => (
                <MessageItem
                  key={msg.id}
                  msg={msg}
                  selectedConversation={selectedConversation}
                  setReplyingTo={setReplyingTo}
                  setPreviewImageUrl={setPreviewImageUrl}
                  setToastMsg={setToastMsg}
                />
              )}
            />
            {/* Message Input Panel */}
            <MessageInputPanel
              selectedConversationId={selectedConversationId}
              selectedConversation={selectedConversation}
              newMessage={newMessage}
              setNewMessage={setNewMessage}
              sending={sending}
              setSending={setSending}
              sendError={sendError}
              setSendError={setSendError}
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
              filePreviewUrl={filePreviewUrl}
              setFilePreviewUrl={setFilePreviewUrl}
              uploadProgress={uploadProgress}
              setUploadProgress={setUploadProgress}
              uploadedMediaUrl={uploadedMediaUrl}
              setUploadedMediaUrl={setUploadedMediaUrl}
              uploading={uploading}
              setUploading={setUploading}
              uploadError={uploadError}
              setUploadError={setUploadError}
              gcsUri={gcsUri}
              setGcsUri={setGcsUri}
              replyingTo={replyingTo}
              setReplyingTo={setReplyingTo}
              waTemplates={waTemplates}
              waTemplatesLoading={waTemplatesLoading}
              waTemplatesError={waTemplatesError}
              setSelectedTemplate={setSelectedTemplate}
              setTemplateModalOpen={setTemplateModalOpen}
              setTemplateVars={setTemplateVars}
              handleSendMessage={handleSendMessage}
              sendMessageWithMedia={sendMessageWithMedia}
              accessToken={accessToken}
              organizationId={selectedConversationDetail?.organization_id || selectedConversation?.organization_id || null}
              setUserAndToken={setUserAndToken}
              messages={messages}
              onMessageSent={() => {
                // Refresh messages after sending
                if (selectedConversationId) {
                  const fetchMessages = async () => {
                    try {
                      const resp = await authFetch(
                        (process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000') +
                        `/api/v1/admin/conversations/${selectedConversationId}/messages?page=1&page_size=${PAGE_SIZE}`,
                        {},
                        accessToken,
                        setUserAndToken
                      );
                      if (resp.ok) {
                        const data = await resp.json();
                        setMessages(data.messages || data.data?.messages || []);
                        setMessagesTotal(data.total || data.data?.total || 0);
                      }
                    } catch (e) {
                      // Silently fail - messages will refresh on next load
                    }
                  };
                  fetchMessages();
                }
              }}
              onConversationUpdate={() => {
                // Refresh conversation detail after updating (e.g., adding new channel)
                if (selectedConversationId) {
                  const fetchConversationDetail = async () => {
                    try {
                      const resp = await authFetch(
                        (process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000') +
                        `/api/v1/admin/conversations/${selectedConversationId}`,
                        {},
                        accessToken,
                        setUserAndToken
                      );
                      if (resp.ok) {
                        const data = await resp.json();
                        setSelectedConversationDetail(data);
                        // Also merge into list copy for UI components that read from selectedConversation
                        setConversations(prev => prev.map(c => (c.id === selectedConversationId ? { ...c, ...data } : c)));
                      }
                    } catch (e) {
                      // Silently fail - conversation will refresh on next load
                    }
                  };
                  fetchConversationDetail();
                }
              }}
            />
            {/* Template Modal */}
            <TemplateMsgModal
              templateModalOpen={templateModalOpen}
              setTemplateModalOpen={setTemplateModalOpen}
              selectedTemplate={selectedTemplate}
              templateVars={templateVars}
              setTemplateVars={setTemplateVars}
              selectedConversationId={selectedConversationId}
              accessToken={accessToken}
              setUserAndToken={setUserAndToken}
              setMessages={setMessages}
              setToastMsg={setToastMsg}
              replyingTo={replyingTo}
              user={user}
            />
            {/* Toast Notification */}
            <ToastNotification
              toastMsg={toastMsg}
              setToastMsg={setToastMsg}
            />
          </div>
        </div>
      </div>
      {previewImageUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80" onClick={() => setPreviewImageUrl(null)}>
          <div className="relative max-w-3xl w-full flex flex-col items-center" onClick={e => e.stopPropagation()}>
            <button
              className="absolute top-4 right-4 text-white bg-black bg-opacity-40 rounded-full p-2 hover:bg-opacity-70 transition"
              onClick={() => setPreviewImageUrl(null)}
              aria-label="Close"
            >
              <svg width="28" height="28" fill="none" viewBox="0 0 24 24"><path d="M6 6l12 12M6 18L18 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
            </button>
            <img src={previewImageUrl} alt="Preview" className="max-h-[80vh] max-w-full rounded-xl shadow-lg" />
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppManager; 